import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import journalEntriesAPI from '../api/journalEntriesAPI';

// Custom debounce hook
function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
}

// Helper to generate local template suggestion
function generateLocalTemplate(context = {}) {
  const { entryDate, vendorName, accountNames = [] } = context;
  
  // Vendor-based template
  if (vendorName && vendorName.trim()) {
    return {
      text: `Vendor adjustment - ${vendorName.trim()}`,
      source: 'template',
      score: 0.95
    };
  }
  
  // Date-based template
  let monthLabel = '';
  if (entryDate) {
    try {
      const date = new Date(entryDate);
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      monthLabel = `${month} ${year}`;
    } catch (e) {
      // ignore invalid date
    }
  }
  
  // Extract keyword from account names
  if (accountNames.length > 0) {
    const firstAccount = accountNames[0];
    // Strip account number and dash (e.g., "5000 - Utilities" -> "Utilities")
    const keyword = firstAccount.replace(/^\d+\s*-\s*/, '').trim().toLowerCase();
    
    if (monthLabel && keyword) {
      return {
        text: `Accrual of ${monthLabel} ${keyword}`,
        source: 'template',
        score: 0.95
      };
    }
  }
  
  // Default templates
  if (monthLabel) {
    return {
      text: `Accrual of ${monthLabel} utilities`,
      source: 'template',
      score: 0.95
    };
  }
  
  return {
    text: 'Reclassification entry',
    source: 'template',
    score: 0.95
  };
}

const PredictiveMemoInput = ({
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Enter memo',
  className = '',
  accountingClientId = null,
  context = {},
  fetchSuggestions = null
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Debounce value
  const debouncedValue = useDebouncedValue(value, 250);
  
  // Compute chosen suggestion
  const chosenSuggestion = useMemo(() => {
    if (activeIndex >= 0 && activeIndex < suggestions.length) {
      return suggestions[activeIndex];
    }
    return suggestions[0] || null;
  }, [activeIndex, suggestions]);
  
  // Compute ghost text
  const ghostText = useMemo(() => {
    if (!open || !chosenSuggestion || !value) return '';
    
    const typed = value;
    const suggestionLower = chosenSuggestion.text.toLowerCase();
    const typedLower = typed.toLowerCase();
    
    if (suggestionLower.startsWith(typedLower)) {
      return chosenSuggestion.text.substring(typed.length);
    }
    
    return '';
  }, [open, chosenSuggestion, value]);
  
  // Fetch suggestions effect
  useEffect(() => {
    if (!open) {
      return;
    }
    
    const fetch = async () => {
      setLoading(true);
      try {
        let results = [];
        
        if (fetchSuggestions) {
          // Use custom fetch function
          results = await fetchSuggestions({
            value: debouncedValue,
            context,
            accountingClientId
          });
        } else if (accountingClientId) {
          // Default: use journal entries API
          const params = new URLSearchParams();
          params.append('accountingClientId', accountingClientId);
          
          const trimmed = (debouncedValue || '').trim();
          if (trimmed) {
            params.append('q', trimmed);
          }
          
          if (context.accounts && context.accounts.length > 0) {
            params.append('accounts', context.accounts.join(','));
          }
          
          if (context.vendorId) {
            params.append('vendorId', context.vendorId);
          }
          
          if (context.vendorName) {
            params.append('vendorName', context.vendorName);
          }
          
          if (context.source) {
            params.append('source', context.source);
          }
          
          if (context.entryDate) {
            params.append('entryDate', context.entryDate);
          }
          
          if (context.accountNames && context.accountNames.length > 0) {
            params.append('accountNames', context.accountNames.join(','));
          }
          
          if (context.entityType) {
            params.append('entityType', context.entityType);
          }
          
          const response = await journalEntriesAPI.getMemoSuggestions(`?${params.toString()}`);
          results = response.suggestions || [];
        } else {
          // Fallback: local template only
          results = [];
        }
        
        // Ensure we have at least a local template
        const localTemplate = generateLocalTemplate(context);
        const hasTemplate = results.some(s => 
          s.text.toLowerCase() === localTemplate.text.toLowerCase()
        );
        
        if (!hasTemplate) {
          results.push(localTemplate);
        }
        
        // Deduplicate by text (case-insensitive)
        const seen = new Map();
        results.forEach(s => {
          const key = s.text.toLowerCase();
          if (!seen.has(key) || (s.score || 0) > (seen.get(key).score || 0)) {
            seen.set(key, s);
          }
        });
        
        // Sort by score desc
        let final = Array.from(seen.values());
        final.sort((a, b) => (b.score || 0) - (a.score || 0));
        
        // Limit to 8
        final = final.slice(0, 8);
        
        setSuggestions(final);
      } catch (err) {
        console.error('Failed to fetch memo suggestions:', err);
        // Fallback to local template
        const localTemplate = generateLocalTemplate(context);
        setSuggestions([localTemplate]);
      } finally {
        setLoading(false);
      }
    };
    
    fetch();
  }, [open, debouncedValue, accountingClientId, context, fetchSuggestions]);
  
  // Apply suggestion
  const applySuggestion = useCallback((suggestion, partialText = null) => {
    if (!suggestion) return;
    
    const textToApply = partialText || suggestion.text;
    onChange(textToApply);
    
    if (partialText === null) {
      // Full suggestion applied, close dropdown
      setOpen(false);
      setActiveIndex(-1);
    }
    
    // Place cursor at end
    if (inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(textToApply.length, textToApply.length);
        }
      });
    }
  }, [onChange]);
  
  // Accept next word (Smart Compose style)
  const acceptNextWord = useCallback(() => {
    if (!chosenSuggestion || !value) return;
    
    const typed = value;
    const suggestionLower = chosenSuggestion.text.toLowerCase();
    const typedLower = typed.toLowerCase();
    
    if (!suggestionLower.startsWith(typedLower)) return;
    
    const remaining = chosenSuggestion.text.substring(typed.length);
    
    // Find next word boundary (word + trailing spaces)
    const match = remaining.match(/^(\S+\s*)/);
    if (match) {
      const nextWord = match[1];
      applySuggestion(chosenSuggestion, typed + nextWord);
    } else {
      // No boundary found, accept full
      applySuggestion(chosenSuggestion);
    }
  }, [chosenSuggestion, value, applySuggestion]);
  
  // Handle keyboard
  const handleKeyDown = useCallback((e) => {
    // Always prevent Enter from submitting form
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIndex >= 0 && suggestions[activeIndex]) {
        applySuggestion(suggestions[activeIndex]);
      }
      return;
    }
    
    if (!open || suggestions.length === 0) {
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
        
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        break;
        
      case 'Tab':
        // Accept full suggestion
        if (chosenSuggestion && ghostText) {
          e.preventDefault();
          applySuggestion(chosenSuggestion);
        }
        break;
        
      case 'ArrowRight': {
        // Accept next word only if caret at end
        const input = inputRef.current;
        if (input && input.selectionStart === input.value.length) {
          if (chosenSuggestion && ghostText) {
            e.preventDefault();
            acceptNextWord();
          }
        }
        break;
      }
        
      default:
        break;
    }
  }, [open, suggestions, activeIndex, chosenSuggestion, ghostText, applySuggestion, acceptNextWord]);
  
  // Handle focus
  const handleFocus = useCallback(() => {
    setOpen(true);
  }, []);
  
  // Handle blur
  const handleBlur = useCallback(() => {
    // Delay to allow click on dropdown
    setTimeout(() => {
      setOpen(false);
      setActiveIndex(-1);
    }, 150);
  }, []);
  
  // Handle change
  const handleChange = useCallback((e) => {
    onChange(e.target.value);
    setActiveIndex(-1);
  }, [onChange]);
  
  // Show dropdown only if we have suggestions and not just showing ghost
  const showDropdown = open && suggestions.length > 0 && !loading;
  
  return (
    <div className="relative">
      {/* Input with ghost text overlay */}
      <div className="relative">
        {/* Ghost text overlay */}
        {ghostText && (
          <div className="absolute inset-0 pointer-events-none flex items-center px-3 py-2 text-gray-400">
            <span className="opacity-0">{value}</span>
            <span>{ghostText}</span>
          </div>
        )}
        
        {/* Actual input */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 relative bg-transparent ${className}`}
          autoComplete="off"
        />
      </div>
      
      {/* Suggestions dropdown */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto"
        >
          <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-200">
            Suggestions {loading && '(Loading...)'}
          </div>
          {suggestions.map((suggestion, idx) => {
            // Determine icon based on source
            let icon = null;
            if (suggestion.source === 'history') {
              // History/clock icon
              icon = (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              );
            } else if (suggestion.source === 'template') {
              // Template/document icon
              icon = (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              );
            } else if (suggestion.source === 'context') {
              // Context/link icon
              icon = (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              );
            }
            
            return (
              <button
                key={idx}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applySuggestion(suggestion)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 border-b border-gray-100 last:border-b-0 ${
                  idx === activeIndex ? 'bg-teal-100' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  {icon && <div className="flex-shrink-0 mt-0.5">{icon}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{suggestion.text}</div>
                    {suggestion.source && (
                      <div className="text-xs text-gray-500 capitalize">{suggestion.source}</div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PredictiveMemoInput;
